import React from "react";
import { func, node, oneOf, oneOfType, string } from "prop-types";

import { PseudoLink } from "../../atoms";
import calloutCardThemes from "../../constants/calloutCardThemes";
import imagePositions from "../../constants/imagePositions";
import * as S from "../styled";

const HorizontalCalloutCardLink = React.forwardRef(
  (
    {
      callToActionCopy,
      description,
      headline,
      href,
      illustration,
      imagePosition,
      onClick,
      theme,
      ...passThroughProps
    },
    ref
  ) => (
    <S.HorizontalCardWrapper
      ref={ref}
      as="a"
      href={href}
      interactive
      onClick={onClick}
      theme={theme}
      {...passThroughProps}
    >
      {illustration && (
        <S.IllustrationWrapper imagePosition={imagePosition}>
          {illustration}
        </S.IllustrationWrapper>
      )}
      <S.ContentWrapper>
        <S.Headline>{headline}</S.Headline>
        {description && <S.Description>{description}</S.Description>}
        <S.CallToAction as={PseudoLink} theme={theme}>
          {callToActionCopy}
        </S.CallToAction>
      </S.ContentWrapper>
    </S.HorizontalCardWrapper>
  )
);

HorizontalCalloutCardLink.propTypes = {
  /**
   * The text inside of the pseudo button within the Callout Card
   */
  callToActionCopy: oneOfType([node, string]).isRequired,
  /**
   * Adds a description block to the Callout Card
   */
  description: string,
  /**
   * Headline for the Callout Card
   */
  headline: string.isRequired,
  /**
   * The URL for the Callout Card.
   * (The entire card is clickable)
   */
  href: string.isRequired,
  /**
   * A component that defines the illustration for the Callout Card.
   * Please use either:
   *   - Brand icon (Images and Icons/Icon/Brand) of size MD
   *   - Avatar of size SM
   */
  illustration: node,
  /**
   * If an illustration is provided, imagePosition will allow to justify it to the right.
   * It is one of:
   *   - LEFT (default)
   *   - RIGHT
   */
  imagePosition: oneOf([imagePositions.LEFT, imagePositions.RIGHT]),
  /**
   * An click callback handler. Can be used for a GA events or other requirements.
   */
  onClick: func,
  /**
   * The Callout Card theme. One of:
   *   - BLUE
   *   - ORANGE
   *   - GRAY
   *   - WHITE
   */
  theme: oneOf(Object.values(calloutCardThemes))
};

HorizontalCalloutCardLink.defaultProps = {
  description: null,
  illustration: null,
  imagePosition: imagePositions.LEFT,
  onClick: () => {},
  theme: calloutCardThemes.WHITE
};

export default HorizontalCalloutCardLink;
